import React from "react";
import { Accordion, Badge, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCcVisa,
  faCcMastercard,
  faCcAmex,
} from "@fortawesome/free-brands-svg-icons";
import { faCreditCard } from "@fortawesome/free-solid-svg-icons";
import SubtleBadge from "components/common/SubtleBadge";
import useFormattedDate from "hooks/useFormatDate";

const brandIcons = {
  visa: faCcVisa,
  mastercard: faCcMastercard,
  amex: faCcAmex,
};

const PaymentMethodAccordion = ({ method, index, activeKey, onToggle }) => {
  const icon = brandIcons[method.merchant] || faCreditCard;

  const formatExpiry = (m, y) => {
    const year = y < 0 ? `20${String(Math.abs(y)).slice(-2)}` : y;
    return `${String(m).padStart(2, "0")}/${year}`;
  };

  const { formatDateTime } = useFormattedDate();

  return (
    <Accordion
      activeKey={activeKey}
      onSelect={() => onToggle(index)}
      className="mb-3 custom-shadow rounded payment-accordion"
    >
      <Accordion.Item eventKey={index} className="border-0">
        {/* header */}
        <Accordion.Header className="p-0">
          <Row className="w-100 align-items-center me-0">
            {/* Brand Icon */}
            <Col xs="auto">
              <div className="p-2 bg-light rounded-circle shadow-sm">
                <FontAwesomeIcon
                  icon={icon}
                  size="lg"
                  className="text-primary"
                />
              </div>
            </Col>

            {/* Summary */}
            <Col>
              <h6 className="text-capitalize fw-semibold">
                {method.merchant} •••• {method.last4}
              </h6>
              <div className="small text-muted">
                Exp: {formatExpiry(method.expiryMonth, method.expiryYear)}
              </div>
            </Col>

            {/* Status + Defaults */}
            <Col
              xs="auto"
              className="text-end d-flex align-items-center"
            >
              <SubtleBadge
                pill
                bg={method.status === "ACTIVE" ? "success" : "secondary"}
              >
                {method.status}
              </SubtleBadge>

              {method.defaultIncomingPaymentMethod && (
                <SubtleBadge pill bg="info" className="ms-1">
                  Incoming Default
                </SubtleBadge>
              )}

              {method.defaultOutgoingPaymentMethod && (
                <SubtleBadge pill bg="warning" className="ms-1 text-dark">
                  Outgoing Default
                </SubtleBadge>
              )}

              <span className="accordion-icon-wrapper">
                <FontAwesomeIcon icon="chevron-down" className="icon-closed" />
                <FontAwesomeIcon icon="chevron-up" className="icon-open" />
              </span>
            </Col>
          </Row>
        </Accordion.Header>

        {/* body */}
        <Accordion.Body>
          {/* Section Title */}
          <h6 className="text-muted mb-3 fw-bold">Payment method details</h6>

          <Row className="gy-3">
            <Col md={6}>
              <label className="text-xxs mb-0">Stripe Payment method ID:</label>
              <h6 className="mb-0 fw-semibold text-sm">
                {method.stripePaymentMethodId}
              </h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0">Merchant:</label>
              <h6 className="mb-0 fw-semibold text-sm">{method.merchant}</h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0">Card:</label>
              <h6 className="mb-0 fw-semibold text-sm">••••{method.last4}</h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0">Expiry:</label>
              <h6 className="mb-0 fw-semibold text-sm">
                {formatExpiry(method.expiryMonth, method.expiryYear)}
              </h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0">Owner name:</label>
              <h6 className="mb-0 fw-semibold text-sm">
                {method.owner.user.firstName} {method.owner.user.lastName}
              </h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0">Owner email:</label>
              <h6 className="mb-0 fw-semibold text-sm">
                {method.owner.user.email}
              </h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0">Date Added:</label>
              <h6 className="mb-0 fw-semibold text-sm">
                {formatDateTime(method.createdAt)}
              </h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0 d-block">Status:</label>
              <SubtleBadge
                pill
                bg={method.status === "ACTIVE" ? "success" : "secondary"}
              >
                {method.status}
              </SubtleBadge>
            </Col>

            {/* Defaults Section */}
            {(method.defaultIncomingPaymentMethod ||
              method.defaultOutgoingPaymentMethod) && (
              <Col md={12} className="pt-2">
                <label className="text-xxs mb-0 d-block">
                  Default Settings:
                </label>

                {method.defaultIncomingPaymentMethod && (
                  <SubtleBadge pill bg="info" className="me-2">
                    Incoming Default
                  </SubtleBadge>
                )}

                {method.defaultOutgoingPaymentMethod && (
                  <SubtleBadge pill bg="warning" className="text-dark">
                    Outgoing Default
                  </SubtleBadge>
                )}
              </Col>
            )}
          </Row>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default PaymentMethodAccordion;
